import type { MissedFeedbackParameters } from 'apiConnector/missed-deal-feedback';
import type { CompetitionParameterFeedback } from 'components/shared/WithErrorTooltip/model';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { PricerId } from 'state/quotes/details/details.model';
import type { PriceEnhancement, PriceEnhancementField } from 'state/quotes/trades/trades.model';

import type { FeedbackAck } from './feedback/model';

export type MissedDealFeedbackAction =
  | MissedDealFeedbackInit
  | MissedDealFeedbackRequested
  | MissedDealFeedbackCommentChanged
  | MissedDealFeedbackSetWithAdditionalInformation
  | MissedDealFeedbackClientTradedChanged
  | MissedDealFeedbackIsSgPositionningChanged
  | MissedDealFeedbackSgPositionChanged
  | MissedDealFeedbackNumberOfIssuersChanged
  | MissedDealFeedbackPriceEnhancedChanged
  | MissedDealFeedbackPriceEnhanced
  | MissedDealFeedbackReset
  | MissedDealFeedbackSubmissionAction
  | MissedDealFeedbackReferentialAction
  | MissedDealCompetitionParameterFeedbackAction;

type MissedDealCompetitionParameterFeedbackAction =
  | MissedDealCompetitionParameterFeedbackChanged
  | MissedDealCompetitionParameterFeedbackRemoved
  | MissedDealCompetitionParameterFeedbackCleared;

type MissedDealFeedbackReferentialAction =
  | MissedDealFeedbackReferentialReceived
  | MissedDealFeedbackReferentialRequestFailed;

type MissedDealFeedbackSubmissionAction =
  | MissedDealFeedbackSubmissionRequested
  | MissedDealFeedbackSubmissionRequestConfirmed
  | MissedDealFeedbackSubmissionRequestDiscarded
  | MissedDealFeedbackSubmitted
  | MissedDealFeedbackSubmissionRequestFailed;

export function missedDealFeedbackInit(tabId: string, pricerId: PricerId) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_INIT' as const,
    tabId,
    pricerId,
  };
}
export type MissedDealFeedbackInit = ReturnType<typeof missedDealFeedbackInit>;
export function missedDealFeedbackRequested(tabId: string) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_REQUESTED' as const,
    tabId,
  };
}
export type MissedDealFeedbackRequested = ReturnType<typeof missedDealFeedbackRequested>;

export function missedDealFeedbackIsSgPositionningChanged(tabId: string, isSgPositionning: boolean) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_IS_SG_POSITIONNING_CHANGED' as const,
    tabId,
    isSgPositionning,
  };
}
export type MissedDealFeedbackIsSgPositionningChanged = ReturnType<typeof missedDealFeedbackIsSgPositionningChanged>;

export function missedDealFeedbackSgPositionChanged(tabId: string, sgPosition?: number) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SG_POSITION_CHANGED' as const,
    tabId,
    sgPosition,
  };
}
export type MissedDealFeedbackSgPositionChanged = ReturnType<typeof missedDealFeedbackSgPositionChanged>;

export function missedDealFeedbackNumberOfIssuersChanged(tabId: string, numberOfIssuers?: number) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_NUMBER_OF_ISSUERS_CHANGED' as const,
    tabId,
    numberOfIssuers,
  };
}
export type MissedDealFeedbackNumberOfIssuersChanged = ReturnType<typeof missedDealFeedbackNumberOfIssuersChanged>;

export function missedDealFeedbackCommentChanged(tabId: string, comment: string) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_COMMENT_CHANGED' as const,
    tabId,
    comment,
  };
}
export type MissedDealFeedbackCommentChanged = ReturnType<typeof missedDealFeedbackCommentChanged>;

export function missedDealFeedbackClientTradedChanged(tabId: string, isClientTraded: boolean) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_CLIENT_TRADED_CHANGED' as const,
    tabId,
    isClientTraded,
  };
}
export type MissedDealFeedbackClientTradedChanged = ReturnType<typeof missedDealFeedbackClientTradedChanged>;

export function missedDealFeedbackPriceEnhancedChanged(tabId: string, isPriceEnhanced: boolean) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_PRICE_ENHANCED_CHANGED' as const,
    tabId,
    isPriceEnhanced,
  };
}
export type MissedDealFeedbackPriceEnhancedChanged = ReturnType<typeof missedDealFeedbackPriceEnhancedChanged>;

export function missedDealFeedbackPriceEnhanced(
  tabId: string,
  field: PriceEnhancementField,
  value: PriceEnhancement[PriceEnhancementField],
) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_PRICE_ENHANCED' as const,
    tabId,
    field,
    value,
  };
}

export type MissedDealFeedbackPriceEnhanced = ReturnType<typeof missedDealFeedbackPriceEnhanced>;
export function missedDealFeedbackReset(tabId: string) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_RESET' as const,
    tabId,
  };
}
export type MissedDealFeedbackReset = ReturnType<typeof missedDealFeedbackReset>;

export function missedDealFeedbackReferentialReceived(tabId: string, parameters: MissedFeedbackParameters) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_REFERENTIAL_RECEIVED' as const,
    tabId,
    parameters,
  };
}
export type MissedDealFeedbackReferentialReceived = ReturnType<typeof missedDealFeedbackReferentialReceived>;

export function missedDealFeedbackReferentialRequestFailed(
  tabId: string,
  technicalError: ApiErrorMessage,
  cause: unknown,
) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_REFERENTIAL_REQUEST_FAILED' as const,
    tabId,
    technicalError,
    cause,
  };
}
export type MissedDealFeedbackReferentialRequestFailed = ReturnType<typeof missedDealFeedbackReferentialRequestFailed>;

export function missedDealCompetitionParameterFeedbackChanged(
  tabId: string,
  index: number,
  feedback: CompetitionParameterFeedback,
) {
  return {
    type: 'MISSED_DEAL_COMPETITION_PARAMETER_FEEDBACK_CHANGED' as const,
    tabId,
    index,
    feedback,
  };
}
export type MissedDealCompetitionParameterFeedbackChanged = ReturnType<
  typeof missedDealCompetitionParameterFeedbackChanged
>;

export function missedDealCompetitionParameterFeedbackRemoved(tabId: string, index: number) {
  return {
    type: 'MISSED_DEAL_COMPETITION_PARAMETER_FEEDBACK_REMOVED' as const,
    tabId,
    index,
  };
}
export type MissedDealCompetitionParameterFeedbackRemoved = ReturnType<
  typeof missedDealCompetitionParameterFeedbackRemoved
>;

export function missedDealCompetitionParameterFeedbackCleared(tabId: string) {
  return {
    type: 'MISSED_DEAL_COMPETITION_PARAMETER_FEEDBACK_CLEARED' as const,
    tabId,
  };
}
export type MissedDealCompetitionParameterFeedbackCleared = ReturnType<
  typeof missedDealCompetitionParameterFeedbackCleared
>;

export function missedDealFeedbackSubmissionRequested(tabId: string) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUESTED' as const,
    tabId,
  };
}
export type MissedDealFeedbackSubmissionRequested = ReturnType<typeof missedDealFeedbackSubmissionRequested>;

export function missedDealFeedbackSubmissionRequestConfirmed(
  tabId: string,
  startDateInMilliseconds: number,
  estimatedTimeInMilliseconds: number,
) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_CONFIRMED' as const,
    tabId,
    startDateInMilliseconds,
    estimatedTimeInMilliseconds,
  };
}
export type MissedDealFeedbackSubmissionRequestConfirmed = ReturnType<
  typeof missedDealFeedbackSubmissionRequestConfirmed
>;

export function missedDealFeedbackSubmissionRequestDiscarded(tabId: string) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_DISCARDED' as const,
    tabId,
  };
}
export type MissedDealFeedbackSubmissionRequestDiscarded = ReturnType<
  typeof missedDealFeedbackSubmissionRequestDiscarded
>;

export function missedDealFeedbackSubmissionRequestFailed(
  tabId: string,
  technicalError: ApiErrorMessage,
  cause: unknown,
) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SUBMISSION_REQUEST_FAILED' as const,
    tabId,
    technicalError,
    cause,
  };
}
export type MissedDealFeedbackSubmissionRequestFailed = ReturnType<typeof missedDealFeedbackSubmissionRequestFailed>;

export function missedDealFeedbackSubmitted(tabId: string, ack: FeedbackAck) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SUBMITTED' as const,
    tabId,
    ack,
  };
}
export type MissedDealFeedbackSubmitted = ReturnType<typeof missedDealFeedbackSubmitted>;

export function missedDealFeedbackSetWithAdditionalInformation(tabId: string, withAdditionalInfo: boolean) {
  return {
    type: 'MISSED_DEAL_FEEDBACK_SET_WITH_ADDITIONAL_INFORMATION' as const,
    tabId,
    withAdditionalInfo,
  };
}
export type MissedDealFeedbackSetWithAdditionalInformation = ReturnType<
  typeof missedDealFeedbackSetWithAdditionalInformation
>;
